
import { useState, useCallback, useRef, useEffect } from "react";
import { isDayAvailable as checkDayIsAvailable } from "@/lib/availability-utils";
import { supabase } from "@/integrations/supabase/client";
import { getDateKeyInTimezone } from "@/lib/timezone/date-utils";

interface BatchAvailabilityState {
  availabilityMap: Map<string, boolean>;
  loadingDates: Set<string>;
  loadedRanges: Array<{ start: Date; end: Date }>;
}

const BATCH_SIZE = 7; // Process 7 days at a time
const CACHE_DURATION = 5 * 1000; // 5 seconds to allow quick cache refresh during fixes

export function useOptimizedDayAvailability(hostUserId?: string, organizerTimezone: string = 'UTC') {
  const [state, setState] = useState<BatchAvailabilityState>({
    availabilityMap: new Map(),
    loadingDates: new Set(),
    loadedRanges: [],
  });

  const batchTimeoutRef = useRef<NodeJS.Timeout>();
  const pendingDatesRef = useRef<Set<Date>>(new Set());

  // Optimized batch processing function with timezone-aware cache keys
  const processBatch = useCallback(async (dates: Date[]) => {
    if (!hostUserId || hostUserId === 'debug') {
      // For debug user, mark all as available
      setState(prev => {
        const newMap = new Map(prev.availabilityMap);
        dates.forEach(date => {
          const dateKey = getDateKeyInTimezone(date, organizerTimezone);
          newMap.set(dateKey, true);
        });
        return {
          ...prev,
          availabilityMap: newMap,
          loadingDates: new Set(),
        };
      });
      return;
    }

    const results = await Promise.allSettled(
      dates.map(async date => {
        try {
          const available = await checkDayIsAvailable(hostUserId, date, supabase);
          const dateKey = getDateKeyInTimezone(date, organizerTimezone);
          return { dateKey, available };
        } catch (error) {
          console.warn(`[useOptimizedDayAvailability] Error checking ${date.toISOString()}:`, error);
          const dateKey = getDateKeyInTimezone(date, organizerTimezone);
          return { dateKey, available: false };
        }
      })
    );

    setState(prev => {
      const newMap = new Map(prev.availabilityMap);
      const newLoadingDates = new Set(prev.loadingDates);
      
      results.forEach((result, index) => {
        const dateKey = getDateKeyInTimezone(dates[index], organizerTimezone);
        newLoadingDates.delete(dateKey);
        
        if (result.status === 'fulfilled') {
          newMap.set(result.value.dateKey, result.value.available);
        } else {
          newMap.set(dateKey, false);
        }
      });

      return {
        ...prev,
        availabilityMap: newMap,
        loadingDates: newLoadingDates,
      };
    });
  }, [hostUserId, organizerTimezone]);

  // Smart batch scheduling with timezone-aware keys
  const scheduleAvailabilityCheck = useCallback((date: Date) => {
    const dateKey = getDateKeyInTimezone(date, organizerTimezone);
    
    // Skip if already loaded or loading
    if (state.availabilityMap.has(dateKey) || state.loadingDates.has(dateKey)) {
      return;
    }

    // Add to pending batch
    pendingDatesRef.current.add(date);
    
    // Mark as loading immediately
    setState(prev => ({
      ...prev,
      loadingDates: new Set([...prev.loadingDates, dateKey]),
    }));

    // Clear existing timeout
    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current);
    }

    // Schedule batch processing
    batchTimeoutRef.current = setTimeout(() => {
      const datesToProcess = Array.from(pendingDatesRef.current);
      pendingDatesRef.current.clear();

      if (datesToProcess.length > 0) {
        // Process in smaller batches
        const batches = [];
        for (let i = 0; i < datesToProcess.length; i += BATCH_SIZE) {
          batches.push(datesToProcess.slice(i, i + BATCH_SIZE));
        }

        // Process batches with small delays
        batches.forEach((batch, index) => {
          setTimeout(() => processBatch(batch), index * 100);
        });
      }
    }, 100); // 100ms debounce
  }, [state.availabilityMap, state.loadingDates, processBatch, organizerTimezone]);

  // Pre-load calendar month
  const preloadCalendarMonth = useCallback((centerDate: Date) => {
    const startDate = new Date(centerDate.getFullYear(), centerDate.getMonth(), 1);
    const endDate = new Date(centerDate.getFullYear(), centerDate.getMonth() + 1, 0);
    
    // Check if this range is already loaded
    const isRangeLoaded = state.loadedRanges.some(range => 
      startDate >= range.start && endDate <= range.end
    );
    
    if (isRangeLoaded) return;

    const dates: Date[] = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }

    // Schedule all dates for checking
    dates.forEach(date => scheduleAvailabilityCheck(date));

    // Mark range as loaded
    setState(prev => ({
      ...prev,
      loadedRanges: [...prev.loadedRanges, { start: startDate, end: endDate }],
    }));
  }, [state.loadedRanges, scheduleAvailabilityCheck]);

  // Get day availability with optimistic loading and timezone-aware cache lookup
  const isDayAvailable = useCallback((date: Date): boolean => {
    const dateKey = getDateKeyInTimezone(date, organizerTimezone);
    
    // Return cached result if available
    if (state.availabilityMap.has(dateKey)) {
      return state.availabilityMap.get(dateKey)!;
    }

    // Schedule check if not already loading
    if (!state.loadingDates.has(dateKey)) {
      scheduleAvailabilityCheck(date);
    }

    // Return optimistic true while loading (better UX)
    return true;
  }, [state.availabilityMap, state.loadingDates, scheduleAvailabilityCheck, organizerTimezone]);

  // Check if date is loading using timezone-aware key
  const isDateLoading = useCallback((date: Date) => {
    const dateKey = getDateKeyInTimezone(date, organizerTimezone);
    return state.loadingDates.has(dateKey);
  }, [state.loadingDates, organizerTimezone]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
    };
  }, []);

  return {
    isDayAvailable,
    preloadCalendarMonth,
    isDateLoading,
    loadedDatesCount: state.availabilityMap.size,
  };
}
